import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiStar, FiClock, FiZap, FiDatabase } from 'react-icons/fi';
import { 
  getOfferings, 
  purchasePackage, 
  isRevenueCatConfigured,
  initRevenueCat,
  logInUser
} from '../utils/revenuecat';
import './SubscriptionPaywall.css';

const SubscriptionPaywall = ({ onClose, onSuccess, user }) => {
  const [loading, setLoading] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    initializeAndLoadOfferings();
  }, [user]);

  const initializeAndLoadOfferings = async () => {
    setInitializing(true);
    setError(null);

    // First, ensure RevenueCat is initialized
    if (!isRevenueCatConfigured()) {
      setError('RevenueCat is not configured. Please contact support.');
      setInitializing(false);
      return;
    }

    try {
      // Initialize RevenueCat if not already initialized
      const initialized = await initRevenueCat(user?.uid || null);
      if (!initialized) {
        console.warn('RevenueCat initialization failed or already initialized, continuing...');
      }

      // If user is logged in, ensure they're logged into RevenueCat
      if (user?.uid) {
        await logInUser(user.uid);
      }

      // Wait a bit to ensure SDK is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Now load offerings
      const offeringsData = await getOfferings();
      console.log('Offerings data:', offeringsData);
      console.log('Offerings data (full):', JSON.stringify(offeringsData, null, 2));
      
      if (offeringsData && offeringsData.current) {
        console.log('Available packages:', offeringsData.current.availablePackages);
        console.log('Package structure:', offeringsData.current.availablePackages.map(pkg => ({
          identifier: pkg.identifier,
          webBillingProduct: pkg.webBillingProduct,
          product: pkg.product,
          rcBillingProduct: pkg.rcBillingProduct,
          productKeys: pkg.webBillingProduct ? Object.keys(pkg.webBillingProduct) : 
                      (pkg.product ? Object.keys(pkg.product) : 'no product')
        })));
        setOfferings(offeringsData.current);
        
        // Default to annual package if available (try both identifier formats)
        const annualPkg = offeringsData.current.availablePackages.find(
          pkg => pkg.identifier === 'pro_annual' || pkg.identifier === '$rc_annual' || pkg.identifier === 'annual'
        );
        if (annualPkg) {
          setSelectedPackage(annualPkg);
        } else if (offeringsData.current.availablePackages.length > 0) {
          setSelectedPackage(offeringsData.current.availablePackages[0]);
        } else {
          setError('No subscription packages found. Please contact support.');
        }
      } else {
        console.error('No current offering found. Offerings:', offeringsData);
        setError('No subscription options available. Please try again later.');
      }
    } catch (err) {
      console.error('Error loading offerings:', err);
      setError(`Failed to load subscription options: ${err.message || 'Please try again.'}`);
    } finally {
      setInitializing(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await purchasePackage(packageId);
      
      if (result.success) {
        // Purchase successful
        if (onSuccess) {
          onSuccess(result);
        }
        // Close modal after short delay to show success
        setTimeout(() => {
          onClose();
        }, 500);
      } else if (result.cancelled) {
        // User cancelled, don't show error
        setLoading(false);
      } else {
        setError(result.error || 'Purchase failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Purchase failed. Please try again.');
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    // Price comes as number in cents or as string with currency
    if (typeof price === 'number') {
      return `$${(price / 100).toFixed(2)}`;
    }
    return price;
  };

  const getPackagePrice = (pkg) => {
    // Add null checks for product structure
    if (!pkg) {
      console.warn('Package is undefined:', pkg);
      return 'N/A';
    }
    
    // For Web Billing, use webBillingProduct
    const product = pkg.webBillingProduct || pkg.product || pkg.rcBillingProduct;
    
    if (!product) {
      console.warn('Product is undefined in package:', pkg);
      return 'N/A';
    }
    
    // Try formattedPrice first (most reliable)
    if (product.currentPrice?.formattedPrice) {
      return product.currentPrice.formattedPrice;
    }
    
    // Try defaultPurchaseOption price
    if (product.defaultPurchaseOption?.pricingPhases?.[0]?.price?.formattedPrice) {
      return product.defaultPurchaseOption.pricingPhases[0].price.formattedPrice;
    }
    
    // Try defaultSubscriptionOption price
    if (product.defaultSubscriptionOption?.basePhase?.price?.formattedPrice) {
      return product.defaultSubscriptionOption.basePhase.price.formattedPrice;
    }
    
    // Fallback: calculate from amountMicros or amount
    if (product.currentPrice?.amountMicros) {
      const price = product.currentPrice.amountMicros / 1000000; // Convert micros to dollars
      return `$${price.toFixed(2)}`;
    }
    
    if (product.currentPrice?.amount) {
      return formatPrice(product.currentPrice.amount); // amount is in cents
    }
    
    return 'N/A';
  };

  const isAnnualPackage = (pkg) => {
    // Check if package is annual (support multiple identifier formats)
    const id = pkg.identifier.toLowerCase();
    return id === 'pro_annual' || id === '$rc_annual' || id === 'annual' || id.includes('annual');
  };

  const getPackagePeriod = (pkg) => {
    if (isAnnualPackage(pkg)) {
      return '/year';
    }
    // Default to monthly
    return '/mo';
  };

  const getMonthlyPrice = (pkg) => {
    // Add null checks
    if (!pkg) {
      console.warn('Package is undefined:', pkg);
      return 'N/A';
    }
    
    // For Web Billing, use webBillingProduct
    const product = pkg.webBillingProduct || pkg.product || pkg.rcBillingProduct;
    
    if (!product) {
      console.warn('Product is undefined in package:', pkg);
      return 'N/A';
    }
    
    if (isAnnualPackage(pkg)) {
      // Annual: calculate monthly equivalent
      let annualPrice = 79.99; // Default fallback
      
      // Try to extract price from product
      if (product.currentPrice?.amountMicros) {
        annualPrice = product.currentPrice.amountMicros / 1000000; // Convert micros to dollars
      } else if (product.currentPrice?.amount) {
        annualPrice = product.currentPrice.amount / 100; // amount is in cents
      } else if (product.defaultPurchaseOption?.pricingPhases?.[0]?.price?.amountMicros) {
        annualPrice = product.defaultPurchaseOption.pricingPhases[0].price.amountMicros / 1000000;
      } else if (product.defaultSubscriptionOption?.basePhase?.price?.amountMicros) {
        annualPrice = product.defaultSubscriptionOption.basePhase.price.amountMicros / 1000000;
      }
      
      const monthlyEquivalent = (annualPrice / 12).toFixed(2);
      return `$${monthlyEquivalent}/mo`;
    }
    // Monthly package - getPackagePrice already returns formatted price with $
    const price = getPackagePrice(pkg);
    if (price === 'N/A') return 'N/A';
    
    // If price already includes $, just append /mo (it should already be formatted)
    return `${price}/mo`;
  };

  const isPopular = (pkg) => {
    // Annual package is usually most popular
    return isAnnualPackage(pkg);
  };

  // Pro features matching your branding
  const proFeatures = [
    {
      icon: <FiZap />,
      title: 'Unlimited generations',
      description: 'Create as many designs as you want, no limits.'
    },
    {
      icon: <FiCheck />,
      title: 'No watermarks',
      description: 'Export clean, professional images without watermarks.'
    },
    {
      icon: <FiStar />,
      title: 'High-resolution exports (4K)',
      description: 'Download your designs in stunning 4K quality.'
    },
    {
      icon: <FiDatabase />,
      title: 'Unlimited storage',
      description: 'Save all your designs without storage limits.'
    }
  ];

  if (initializing || !offerings || !offerings.availablePackages || offerings.availablePackages.length === 0) {
    return (
      <div className="paywall-overlay" onClick={onClose}>
        <div className="paywall-content" onClick={(e) => e.stopPropagation()}>
          <button className="paywall-close-btn" onClick={onClose}>
            <FiX />
          </button>
          <div className="paywall-error">
            {initializing ? 'Loading subscription options...' : (error || 'No subscription options available. Please try again later.')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paywall-overlay" onClick={onClose}>
      <div className="paywall-content" onClick={(e) => e.stopPropagation()}>
        <button className="paywall-close-btn" onClick={onClose}>
          <FiX />
        </button>

        <div className="paywall-header">
          <h2>Upgrade to Pro</h2>
        </div>

        {error && (
          <div className="paywall-error-message">
            {error}
          </div>
        )}

        {/* Features List */}
        <div className="paywall-features-list">
          {proFeatures.map((feature, index) => (
            <div key={index} className="paywall-feature-item">
              <div className="paywall-feature-icon">
                {feature.icon}
              </div>
              <div className="paywall-feature-content">
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Options */}
        <div className="paywall-pricing-options">
          {offerings.availablePackages.map((pkg) => {
            const isAnnual = isAnnualPackage(pkg);
            const isSelected = selectedPackage?.identifier === pkg.identifier;
            const popular = isPopular(pkg);

            return (
              <div
                key={pkg.identifier}
                className={`pricing-option-box ${isSelected ? 'selected' : ''} ${popular ? 'popular' : ''}`}
                onClick={() => setSelectedPackage(pkg)}
              >
                <div className="pricing-option-header">
                  <input
                    type="radio"
                    name="subscription-plan"
                    checked={isSelected}
                    onChange={() => setSelectedPackage(pkg)}
                    className="pricing-radio"
                  />
                  <div className="pricing-option-details">
                    <span className="pricing-plan-name">{isAnnual ? 'Annual' : 'Monthly'}</span>
                    {popular && (
                      <span className="pricing-savings-badge">Save $20/year</span>
                    )}
                  </div>
                  <div className="pricing-option-price">
                    <span className="pricing-monthly">{getMonthlyPrice(pkg)}</span>
                    {isAnnual && (
                      <span className="pricing-total">{getPackagePrice(pkg)}/year</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Upgrade Button */}
        <button
          className="paywall-upgrade-btn"
          onClick={() => selectedPackage && handlePurchase(selectedPackage.identifier)}
          disabled={loading || !selectedPackage}
        >
          {loading ? 'Processing...' : 'Upgrade Now'}
        </button>

        {/* Footer Note */}
        <div className="paywall-footer-note">
          <p>Cancel anytime. Subscription automatically renews.</p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaywall;
